/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, GetArrow, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvLegendLabels, rvTooltipStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartStatTiles } from '../ChartStatTiles';
import { ChartEmptyState } from '../ChartEmptyState';
import AccessTimeOutlinedIcon from '@material-ui/icons/AccessTimeOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            [`& .${`titleWrapper`}`]: {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                [`& .${`title`}`]: {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`value`}`]: {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`secondary-title`}`]: {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        barTitleContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
            [`& .${`h1`}`]: {
                display: 'flex',
                alignItems: 'center',
            },
            [`& .${`h2`}`]: {
                fontSize: '2.5rem',
                padding: '10px 0',
            },
            [`& .${`h3`}`]: {
                paddingTop: '10px',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            padding: '0 15px',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
    }),
);

export enum LastCountEnum {
    LAST_14_DAYS,
    LAST_30_DAYS,
    LAST_12_MOTNHS,
    LAST_WEEK,
}

interface TimeAndFeesBilledInLastFinancialYearChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    last: LastCountEnum;
    months: string[];
    fees: number[];
    billedTime: number[];
    unbilledTime: number[];
    currentTotal: number;
    periodBilled: number;
    periodUnbilled: number;
    previousTotal: number;
    periodString: string;
    title: string;
    symbol: string;
}

interface TimeTimeAndFeesData {
    statistics: Statistics;
}

interface Statistics {
    time: TimeStatistics;
}

interface TimeStatistics {
    timeAndFeesRecordedInPeriod: TimeAndFeesRecordedInPeriod;
}

interface TimeAndFeesRecordedInPeriod {
    last12Months: TimeData;
    last14Days: TimeData;
    last30Days: TimeData;
    lastWeek: TimeData;
}

interface TimeData {
    summary: TimeDataSummary;
    previousPeriodSummary: TimeDataPreviousPeriodSummary;
    timeAndFeesPeriodItem: [GroupData];
}

interface TimeDataSummary {
    totalTime: number;
    totalFees: number;
    unbilled: number;
    billed: number;
}

interface TimeDataPreviousPeriodSummary {
    previousTotalTime: number;
    previousTotalFees: number;
}

interface GroupData {
    group: string;
    billedTime: number;
    unbilledTime: number;
    fees: number;
}

export const TimeAndFeesRecordedInPeriodChart: React.FC<TimeAndFeesBilledInLastFinancialYearChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line: no-any
    const chartRef = React.useRef<any>();

    const [state, setState] = useState<ChartState>({
        last: LastCountEnum.LAST_WEEK,
        months: [],
        fees: [],
        billedTime: [],
        unbilledTime: [],
        currentTotal: 0,
        previousTotal: 0,
        periodBilled: 0,
        periodUnbilled: 0,
        periodString: 'Week',
        title: 'this week',
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<TimeTimeAndFeesData>(ChartData, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let months: string[] = [];
                let billedTime: number[] = [];
                let unbilledTime: number[] = [];
                let fees: number[] = [];
                let previousTotal: number = 0;
                let currentTotal: number = 0;
                let periodString: string = '';
                let periodUnbilled: number = 0;
                let periodBilled: number = 0;
                let title: string = '';
                if (!error && data) {
                    if (state.last === LastCountEnum.LAST_WEEK) {
                        var lastWeek = data.statistics.time.timeAndFeesRecordedInPeriod.lastWeek;
                        months = lastWeek.timeAndFeesPeriodItem.map((s) => s.group);
                        billedTime = lastWeek.timeAndFeesPeriodItem.map((s) => s.billedTime);
                        unbilledTime = lastWeek.timeAndFeesPeriodItem.map((s) => s.unbilledTime);
                        fees = lastWeek.timeAndFeesPeriodItem.map((s) => s.fees);
                        currentTotal = lastWeek.summary.totalFees;
                        previousTotal = lastWeek.previousPeriodSummary.previousTotalFees;
                        periodBilled = lastWeek.summary.billed;
                        periodUnbilled = lastWeek.summary.unbilled;
                        periodString = 'Week';
                        title = 'this week';
                    } else if (state.last === LastCountEnum.LAST_14_DAYS) {
                        var last14Days = data.statistics.time.timeAndFeesRecordedInPeriod.last14Days;
                        months = last14Days.timeAndFeesPeriodItem.map((s) => s.group);
                        billedTime = last14Days.timeAndFeesPeriodItem.map((s) => s.billedTime);
                        unbilledTime = last14Days.timeAndFeesPeriodItem.map((s) => s.unbilledTime);
                        fees = last14Days.timeAndFeesPeriodItem.map((s) => s.fees);
                        currentTotal = last14Days.summary.totalFees;
                        previousTotal = last14Days.previousPeriodSummary.previousTotalFees;
                        periodBilled = last14Days.summary.billed;
                        periodUnbilled = last14Days.summary.unbilled;
                        periodString = '14 Days';
                        title = 'in last 14 Days';
                    } else if (state.last === LastCountEnum.LAST_30_DAYS) {
                        var last30Days = data.statistics.time.timeAndFeesRecordedInPeriod.last30Days;
                        months = last30Days.timeAndFeesPeriodItem.map((s) => s.group);
                        billedTime = last30Days.timeAndFeesPeriodItem.map((s) => s.billedTime);
                        unbilledTime = last30Days.timeAndFeesPeriodItem.map((s) => s.unbilledTime);
                        fees = last30Days.timeAndFeesPeriodItem.map((s) => s.fees);
                        currentTotal = last30Days.summary.totalFees;
                        previousTotal = last30Days.previousPeriodSummary.previousTotalFees;
                        periodBilled = last30Days.summary.billed;
                        periodUnbilled = last30Days.summary.unbilled;
                        periodString = '30 Days';
                        title = 'in last 30 Days';
                    } else {
                        var last12Months = data.statistics.time.timeAndFeesRecordedInPeriod.last12Months;
                        months = last12Months.timeAndFeesPeriodItem.map((s) => s.group);
                        billedTime = last12Months.timeAndFeesPeriodItem.map((s) => s.billedTime);
                        unbilledTime = last12Months.timeAndFeesPeriodItem.map((s) => s.unbilledTime);
                        fees = last12Months.timeAndFeesPeriodItem.map((s) => s.fees);
                        currentTotal = last12Months.summary.totalFees;
                        previousTotal = last12Months.previousPeriodSummary.previousTotalFees;
                        periodBilled = last12Months.summary.billed;
                        periodUnbilled = last12Months.summary.unbilled;
                        periodString = '12 Months';
                        title = 'in last 12 Months';
                    }
                }

                return {
                    ...prevState,
                    months,
                    billedTime,
                    unbilledTime,
                    fees,
                    previousTotal,
                    currentTotal,
                    periodBilled,
                    periodUnbilled,
                    periodString,
                    title,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [state.last, loading]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('IsTimesheetTimeAndFeesInLastPeriodsChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const handleTotalClick = (isCurrent: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            if (state.last === LastCountEnum.LAST_WEEK) {
                // This Week
                searchCriterias.push({ property: 'ThisWeek', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev' });
            } else if (state.last === LastCountEnum.LAST_14_DAYS) {
                searchCriterias.push({ property: 'PeriodByDays', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev', period: '14' });
            } else if (state.last === LastCountEnum.LAST_30_DAYS) {
                searchCriterias.push({ property: 'PeriodByDays', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev', period: '30' });
            } else {
                searchCriterias.push({ property: '12MonthPeriod', operator: 'eq', comparison: isCurrent ? 'curr' : 'prev' });
            }

            searchCriterias.push({ property: 'Amount', operator: 'ne', comparison: '0' });

            props.getSelectedItem(searchCriterias, false, 'Time Recorded', DrillDownType.timesheetSummary, 'Timesheet Lines');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.yFees.min = 0;
        chartRef.current.options.scales.yTime.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (loading || currencySymbolQuery.loading) {
        return (
            <ChartSkeleton variant="columns" />
        );
    }
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.billedTime, state.unbilledTime, state.fees) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo 
                    description={props.description}
                    fontSize={'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.barTitleContainer}>
                <ChartHeading icon={<AccessTimeOutlinedIcon />} title={<>Timesheets {state.title}</>} />
                <div className="h2">
                    <div className={classes.totalDiv} onClick={() => handleTotalClick(true)}>
                        <AnimatedNumber value={state.currentTotal} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                        {GetArrow(state.currentTotal, state.previousTotal)}
                    </div>
                </div>
                <ChartStatTiles
                    tiles={[
                        { label: 'Billable hrs', value: formatNumber(state.periodBilled), color: RV_CHART_COLORS.primary },
                        { label: 'Non-billable hrs', value: formatNumber(state.periodUnbilled), color: RV_CHART_COLORS.primarySoft },
                        { label: `Previous ${state.periodString}`, value: `${state.symbol}${formatNumber(state.previousTotal)}`, onClick: () => handleTotalClick(false) },
                    ]}
                />
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;
                            const _datasetIndex = getElementAtEvent(chartRef.current, event)[0].datasetIndex;

                            const item = state.months[_index];

                            const searchCriterias = new Array<SearchCriteria>();
                            // var d = new Date();
                            // var year = d.getFullYear();
                            if (state.last === LastCountEnum.LAST_WEEK) {
                                searchCriterias.push({ property: 'Day', operator: 'eq', comparison: item });
                            } else if (state.last === LastCountEnum.LAST_14_DAYS) {
                                searchCriterias.push({ property: 'DateShortString', operator: 'eq', comparison: item });
                            } else if (state.last === LastCountEnum.LAST_30_DAYS) {
                                searchCriterias.push({ property: 'DateShortString', operator: 'eq', comparison: item });
                            } else {
                                searchCriterias.push({ property: 'MonthStart', operator: 'gteq', comparison: item }, { property: 'MonthEnd', operator: 'lteq', comparison: item });
                            }
                            // tslint:disable-next-line:no-console
                            // console.log(index);
                            // . e.g. - Apr 22, Apr 23, etc
                            searchCriterias.push({ property: 'Amount', operator: _datasetIndex === 1 ? 'eq' : 'ne', comparison: '0' });

                            props.getSelectedItem(searchCriterias, false, 'Time Recorded - ' + item, DrillDownType.timesheetSummary, 'Timesheet Lines');
                        }
                    }}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                fill: false,
                                label: 'Billable Time',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.billedTime,
                                type: 'bar',
                                yAxisID: 'yTime',
                            },
                            {
                                fill: false,
                                label: 'Non-Billable Time',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primarySoft),
                                data: state.unbilledTime,
                                type: 'bar',
                                yAxisID: 'yTime',
                            },

                            {
                                fill: false,
                                label: 'Fees Recorded',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.amber),
                                data: state.fees,
                                type: 'bar',
                                yAxisID: 'yFees',
                            },
                            // tslint:disable-next-line: no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            yTime: {
                                position: 'left',
                                axis: 'y',
                                suggestedMax: getSuggestedMaxTick([...state.billedTime, ...state.unbilledTime]),
                                beginAtZero: true,
                                ticks: {
                                    maxTicksLimit: getMaxTickLimit([...state.billedTime, ...state.unbilledTime]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)} hrs`;
                                    },
                                },
                            },
                            yFees: {
                                position: 'right',
                                axis: 'y',
                                suggestedMax: getSuggestedMaxTick(state.fees),
                                beginAtZero: true,
                                ticks: {
                                    maxTicksLimit: getMaxTickLimit(state.fees),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                // max: 9 // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: true,
                                labels: rvLegendLabels,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);
                                        // return `${dataset.label} ${symbol}${formatNumber(currentValue)}`;
                                        if (dataset.label.toLowerCase() === 'fees recorded') {
                                            return `${dataset.label} ${state.symbol}${formatNumber(currentValue)}`;
                                        } else {
                                            return `${dataset.label} ${formatNumber(currentValue, 2)} hrs`;
                                        }
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.billedTime, state.unbilledTime, state.fees) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LastCountEnum.LAST_WEEK} control={<Radio color="primary" />} label="This Week" />
                    <FormControlLabel value={LastCountEnum.LAST_14_DAYS} control={<Radio color="primary" />} label="Last 14 days" />
                    <FormControlLabel value={LastCountEnum.LAST_30_DAYS} control={<Radio color="primary" />} label="Last 30 days" />
                    <FormControlLabel value={LastCountEnum.LAST_12_MOTNHS} control={<Radio color="primary" />} label="Last 12 months" />
                </RadioGroup>
            </FormControl>
        </div>
    );
};

// tslint:disable-next-line:no-any

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

const ChartData = gql`
    query TimeAndFeesRecordedInPeriodChartData($entityType: Int, $entityGuid: String, $responsibility: Int) {
        statistics {
            time {
                timeAndFeesRecordedInPeriod(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                    last14Days {
                        timeAndFeesPeriodItem {
                            billedTime
                            unbilledTime
                            fees
                            group
                        }
                        summary {
                            totalFees
                            totalTime
                            unbilled
                            billed
                        }
                        previousPeriodSummary {
                            previousTotalFees
                            previousTotalTime
                        }
                    }
                    last30Days {
                        timeAndFeesPeriodItem {
                            billedTime
                            unbilledTime
                            fees
                            group
                        }
                        summary {
                            totalFees
                            totalTime
                            unbilled
                            billed
                        }
                        previousPeriodSummary {
                            previousTotalFees
                            previousTotalTime
                        }
                    }
                    last12Months {
                        timeAndFeesPeriodItem {
                            billedTime
                            unbilledTime
                            fees
                            group
                        }
                        summary {
                            totalFees
                            totalTime
                            unbilled
                            billed
                        }
                        previousPeriodSummary {
                            previousTotalFees
                            previousTotalTime
                        }
                    }
                    lastWeek {
                        timeAndFeesPeriodItem {
                            billedTime
                            unbilledTime
                            fees
                            group
                        }
                        summary {
                            totalFees
                            totalTime
                            unbilled
                            billed
                        }
                        previousPeriodSummary {
                            previousTotalFees
                            previousTotalTime
                        }
                    }
                }
            }
        }
    }
`;
